import { useState } from 'react';
import { useAuth } from './AuthContext';
import './Login.css';

// The server's own wording is shown, so the page never says more than the server decided.
function friendly(message) {
  const text = String(message || '');
  if (text.startsWith('429')) return 'Too many wrong passwords. Try again in 15 minutes.';
  if (text.startsWith('401')) return 'The email or password is not right.';
  if (text.startsWith('403')) return 'This account is not allowed to use MenaGTM.';
  return 'Could not sign in. Check the connection and try again.';
}

// The picture beside the form: what the console shows, not live data.
const PREVIEW = [
  ['N', 'Northwind Robotics', 'Hiring 6 annotators · Series B', 87],
  ['B', 'Bluefin Health AI', 'Labeling RFP posted', 81],
  ['Q', 'Quarry Vision', 'New VP of Data', 74],
];

export default function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(friendly(err.message));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-side">
        <div className="login-brand">
          <svg width="34" height="34" viewBox="0 0 64 64" aria-hidden="true"><rect width="64" height="64" rx="16" fill="#17233C" /><path d="M14 44V22l10 12 8-14 8 14 10-12v22" fill="none" stroke="#16A36A" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" /><circle cx="32" cy="20" r="3.5" fill="#FFFFFF" /></svg>
          MenaGTM
        </div>
        <form className="login-card" onSubmit={submit}>
          <h1>Welcome back</h1>
          <p className="login-sub">Sign in to the MenaDevs go-to-market console.</p>
          <label>
            Email
            <input type="email" autoComplete="username" placeholder="name@menadevs.io" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <div className="login-field">
            <label htmlFor="login-password">Password</label>
            <span className="login-password">
              <input id="login-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" className="login-show" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </span>
          </div>
          {error && <div className="login-error" role="alert">{error}</div>}
          <button type="submit" className="login-submit" disabled={busy || !email.trim() || !password}>{busy ? 'Signing in…' : 'Sign in'}</button>
          <div className="login-note">Access is by invitation. Ask a MenaGTM admin to add your account.</div>
        </form>
        <div className="login-foot"><span>© {new Date().getFullYear()} MenaDevs</span><span>Internal use only</span></div>
      </section>
      <section className="login-art" aria-hidden="true">
        <div className="login-kicker">Signal to pipeline</div>
        <h2>See who is buying, why now, and who to talk to.</h2>
        <div className="login-preview">
          <div className="login-preview-bar"><i /><i /><i /><span>Accounts · Tier A</span></div>
          {PREVIEW.map(([letter, name, why, score]) => (
            <div className="login-preview-row" key={name}>
              <span className="login-preview-mark">{letter}</span>
              <span className="login-preview-name"><strong>{name}</strong><small>{why}</small></span>
              <span className="login-preview-meter"><span style={{ width: `${score}%` }} /></span>
              <span className="login-preview-score">{score}</span>
            </div>
          ))}
        </div>
        <div className="login-points"><span>Live buying signals</span><span>Explainable scores</span><span>Verified contacts</span></div>
      </section>
    </main>
  );
}
